import type { IsoDateTime, LocalDateKey, XpEvent, XpSourceType } from '@/types';
import { createId } from './ids';

export const XP_REWARDS = {
  normalTask: 10,
  focusBlock: 20,
  mandatoryTask: 30,
  schoolGoal: 40,
  frenchGoal: 40,
  sportGoal: 40,
  novelGoal: 50,
  youtubeGoal: 30,
  completeDay: 100
} as const;

export const XP_PER_LEVEL = 250;

export interface XpAwardInput {
  sourceType: XpSourceType;
  sourceId: string;
  date: LocalDateKey;
  amount: number;
  reason: string;
  sourceKey?: string;
  awardedAt?: IsoDateTime;
}

export interface LevelProgress {
  level: number;
  name: LevelName;
  totalXp: number;
  currentLevelXp: number;
  xpForNextLevel: number;
  percent: number;
}

export type LevelName =
  'Start' | 'Beständig' | 'Diszipliniert' | 'Konsequent' | 'Unaufhaltsam' | 'Meisterschaft';

export function makeXpSourceKey(sourceType: XpSourceType, sourceId: string): string {
  return `${sourceType}:${sourceId}`;
}

export function awardXpOnce(
  existingEvents: readonly XpEvent[],
  input: XpAwardInput
): XpEvent | undefined {
  if (!Number.isFinite(input.amount) || input.amount < 0) {
    throw new RangeError('XP award must be a finite, non-negative number');
  }
  const sourceKey = input.sourceKey ?? makeXpSourceKey(input.sourceType, input.sourceId);
  if (existingEvents.some((event) => event.sourceKey === sourceKey)) return undefined;
  const now = input.awardedAt ?? new Date().toISOString();
  return {
    id: createId(),
    sourceKey,
    sourceType: input.sourceType,
    sourceId: input.sourceId,
    date: input.date,
    amount: input.amount,
    reason: input.reason,
    createdAt: now,
    updatedAt: now
  };
}

/** Keeps one event per logical source and selects its most recently updated value. */
export function deduplicateXpEvents(events: readonly XpEvent[]): XpEvent[] {
  const bySource = new Map<string, XpEvent>();
  for (const event of events) {
    const previous = bySource.get(event.sourceKey);
    if (!previous || event.updatedAt > previous.updatedAt) bySource.set(event.sourceKey, event);
  }
  return [...bySource.values()];
}

export function correctXpAward(
  events: readonly XpEvent[],
  sourceKey: string,
  correctedAmount: number,
  correctedAt: IsoDateTime = new Date().toISOString()
): XpEvent[] {
  if (!Number.isFinite(correctedAmount) || correctedAmount < 0) {
    throw new RangeError('Corrected XP must be a finite, non-negative number');
  }
  const deduplicated = deduplicateXpEvents(events);
  const target = deduplicated.find((event) => event.sourceKey === sourceKey);
  if (!target || target.amount === correctedAmount) return deduplicated;
  return deduplicated.map((event) =>
    event.sourceKey === sourceKey
      ? { ...event, amount: correctedAmount, correctedAt, updatedAt: correctedAt }
      : event
  );
}

export function calculateTotalXp(events: readonly XpEvent[]): number {
  return deduplicateXpEvents(events).reduce((total, event) => total + event.amount, 0);
}

export function levelForXp(xp: number): number {
  if (!Number.isFinite(xp)) throw new RangeError('XP must be finite');
  return Math.floor(Math.max(0, xp) / XP_PER_LEVEL) + 1;
}

export function levelName(level: number): LevelName {
  if (level >= 50) return 'Meisterschaft';
  if (level >= 35) return 'Unaufhaltsam';
  if (level >= 20) return 'Konsequent';
  if (level >= 10) return 'Diszipliniert';
  if (level >= 5) return 'Beständig';
  return 'Start';
}

export function getLevelProgress(totalXp: number): LevelProgress {
  const normalized = Math.max(0, totalXp);
  const level = levelForXp(normalized);
  const currentLevelXp = normalized - (level - 1) * XP_PER_LEVEL;
  return {
    level,
    name: levelName(level),
    totalXp: normalized,
    currentLevelXp,
    xpForNextLevel: XP_PER_LEVEL,
    percent: Math.floor((currentLevelXp / XP_PER_LEVEL) * 100)
  };
}
